import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import RoleBasedNavigation from '../../components/navigation/RoleBasedNavigation';
import WelcomeHeader from './components/WelcomeHeader';
import ProgressOverview from './components/ProgressOverview';
import PlayCard from './components/PlayCard';
import BadgeCollection from './components/BadgeCollection';
import RecentAchievements from './components/RecentAchievements';
import LeaderboardPanel from './components/LeaderboardPanel';
import QuickActions from './components/QuickActions';
import { useAuth } from '../../contexts/AuthContext';
import { 
  getStudentDashboardData, 
  getStudentAchievements, 
  getLeaderboard,
  claimDailyXpBonus
} from '../../services/playsService';
import { RealtimeSubscriptionService } from '../../services/realtimeSubscriptionService';
import { supabase } from '../../lib/supabase';
import { getAllStudentAssets, getUserAvatarConfig, buildAvatarLayers } from '../../services/avatarBuilderService';
import CelebrationModal from '../../components/CelebrationModal';
import useCelebration from '../../hooks/useCelebration';
import useToast from '../../hooks/useToast';
import ToastContainer from '../../components/ToastContainer';

const StudentStageDashboard = () => {
  const { user, userProfile, loading, profileLoading } = useAuth();
  const navigate = useNavigate();
  const [realtimeService] = useState(() => new RealtimeSubscriptionService());
  const [liveNotifications, setLiveNotifications] = useState([]);
  const { celebration, triggerBadge, triggerStreak, triggerPlayComplete, dismissCelebration } = useCelebration();
  const { toasts, toastBadge, toastStreak, toastAchievement, dismiss: dismissToast } = useToast();

  // State for dashboard data
  const [recentPlays, setRecentPlays] = useState([]);
  const [availablePlays, setAvailablePlays] = useState([]);
  const [badges, setBadges] = useState([]);
  const [achievements, setAchievements] = useState([]);
  const [leaderboardUsers, setLeaderboardUsers] = useState([]);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dailyBonusClaimed, setDailyBonusClaimed] = useState(false);
  const [currentXp, setCurrentXp] = useState(0);

  // Avatar preview state
  const [hasExistingAvatar, setHasExistingAvatar] = useState(false);
  const [randomAvatarLayers, setRandomAvatarLayers] = useState([]);
  const [savedAvatarLayers, setSavedAvatarLayers] = useState([]);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user?.id) return;

      try {
        setDashboardLoading(true);
        setError(null);

        // Fetch plays and progress
        const playsData = await getStudentDashboardData(user?.id);
        setRecentPlays(playsData?.recentPlays || []);
        setAvailablePlays(playsData?.availablePlays || []);

        // Fetch achievements and badges
        const achievementsData = await getStudentAchievements(user?.id);
        setBadges(achievementsData?.badges || []);
        setAchievements(achievementsData?.achievements || []);

        // Fetch leaderboard
        const leaderboardData = await getLeaderboard(5);
        setLeaderboardUsers(leaderboardData || []);

        // Check if user already has a saved avatar
        const existingConfig = await getUserAvatarConfig(user?.id);
        if (existingConfig?.avatar_config) {
          setHasExistingAvatar(true);
          // Load all assets to build the saved avatar preview layers
          const allAssets = await getAllStudentAssets();
          const assetsByCategorySlug = {};
          for (const asset of allAssets) {
            const slug = asset?.avatar_asset_categories?.slug;
            if (slug) {
              if (!assetsByCategorySlug?.[slug]) assetsByCategorySlug[slug] = [];
              assetsByCategorySlug?.[slug]?.push(asset);
            }
          }
          const savedLayers = buildAvatarLayers(existingConfig?.avatar_config, assetsByCategorySlug);
          setSavedAvatarLayers(savedLayers);
        } else {
          // Build a random avatar preview
          const allAssets = await getAllStudentAssets();
          const assetsByCategorySlug = {};
          for (const asset of allAssets) {
            const slug = asset?.avatar_asset_categories?.slug;
            if (slug) {
              if (!assetsByCategorySlug?.[slug]) assetsByCategorySlug[slug] = [];
              assetsByCategorySlug?.[slug]?.push(asset);
            }
          }
          const CATEGORY_KEYS = [
            { slug: 'backgrounds', configKey: 'background' },
            { slug: 'bodies', configKey: 'bodyType' },
            { slug: 'eyes', configKey: 'eyes' },
            { slug: 'eyebrows', configKey: 'eyebrows' },
            { slug: 'mouths', configKey: 'mouth' },
            { slug: 'hair_styles', configKey: 'hairStyle' },
            { slug: 'clothing', configKey: 'clothing' },
          ];
          const randomConfig = {};
          for (const { slug, configKey } of CATEGORY_KEYS) {
            const assets = assetsByCategorySlug?.[slug] || [];
            if (assets?.length > 0) {
              const pick = assets?.[Math.floor(Math.random() * assets?.length)];
              randomConfig[configKey] = pick?.slug;
            }
          }
          const layers = buildAvatarLayers(randomConfig, assetsByCategorySlug);
          setRandomAvatarLayers(layers);
        }

      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError(err?.message || 'Failed to load dashboard data');
      } finally {
        setDashboardLoading(false);
      }
    };

    fetchDashboardData();
  }, [user?.id]);

  // Claim daily XP bonus once per day
  useEffect(() => {
    const handleDailyBonus = async () => {
      if (!user?.id) return;
      const result = await claimDailyXpBonus(user?.id);
      if (result?.claimed) {
        setDailyBonusClaimed(true);
        setCurrentXp(result?.newXp);
      } else {
        setCurrentXp(result?.currentXp || userProfile?.xp_points || 0);
      }
    };
    if (user?.id && !profileLoading) {
      handleDailyBonus();
    }
  }, [user?.id, profileLoading]);

  // Keep currentXp in sync with userProfile when bonus not yet evaluated
  useEffect(() => {
    if (!dailyBonusClaimed && userProfile?.xp_points !== undefined) {
      setCurrentXp(userProfile?.xp_points || 0);
    }
  }, [userProfile?.xp_points, dailyBonusClaimed]);

  // Set up real-time subscriptions for student's own achievements
  useEffect(() => {
    if (!user?.id) return;

    // Subscribe to badges
    const unsubscribeBadges = realtimeService?.subscribeToAchievementBadges(
      async (update) => {
        console.log('Real-time badge update:', update);
        
        // Fetch the new badge details
        const { data: badge } = await supabase
          ?.from('achievement_badges')
          ?.select('*')
          ?.eq('id', update?.data?.id)
          ?.single();
        
        if (badge) {
          // Add to badges list
          setBadges(prev => [badge, ...prev]);
          
          // Trigger badge celebration modal
          triggerBadge({ name: badge?.badge_name, description: badge?.description });

          // Show toast notification
          toastBadge(badge?.badge_name);

          // Create notification
          const notification = {
            id: `badge-${Date.now()}`,
            type: 'badge',
            title: 'Badge Earned!',
            message: `You earned the "${badge?.badge_name}" badge!`,
            timestamp: new Date(),
            read: false
          };
          setLiveNotifications(prev => [notification, ...prev]?.slice(0, 10));
        }
      },
      user?.id
    );

    // Subscribe to achievements
    const unsubscribeAchievements = realtimeService?.subscribeToUserAchievements(
      async (update) => {
        console.log('Real-time achievement update:', update);
        
        // Fetch achievement details
        const { data: userAchievement } = await supabase
          ?.from('user_achievements')
          ?.select(`
            *,
            achievements (
              name,
              description,
              badge_icon,
              xp_reward
            )
          `)
          ?.eq('id', update?.data?.id)
          ?.single();
        
        if (userAchievement) {
          // Add to achievements list
          setAchievements(prev => [userAchievement, ...prev]);

          // Show toast notification
          toastAchievement(userAchievement?.achievements?.name);
          
          // Create notification
          const notification = {
            id: `achievement-${Date.now()}`,
            type: 'achievement',
            title: 'Achievement Unlocked!',
            message: `You unlocked "${userAchievement?.achievements?.name}"!`,
            timestamp: new Date(),
            read: false
          };
          setLiveNotifications(prev => [notification, ...prev]?.slice(0, 10));
        }
      },
      user?.id
    );

    // Subscribe to quiz completions
    const unsubscribeQuizzes = realtimeService?.subscribeToQuizCompletions(
      async (update) => {
        console.log('Real-time quiz completion:', update);
        
        const score = update?.data?.score || 0;
        const totalQuestions = update?.data?.total_questions || 0;
        const percentage = totalQuestions > 0 ? Math.round((score / totalQuestions) * 100) : 0;
        const passed = update?.data?.passed;
        
        // Create notification
        const notification = {
          id: `quiz-${Date.now()}`,
          type: passed ? 'success' : 'info',
          title: passed ? 'Quiz Passed!' : 'Quiz Completed',
          message: `You scored ${percentage}% on the quiz`,
          timestamp: new Date(),
          read: false
        };
        setLiveNotifications(prev => [notification, ...prev]?.slice(0, 10));
      },
      user?.id
    );

    return () => {
      if (unsubscribeBadges) unsubscribeBadges();
      if (unsubscribeAchievements) unsubscribeAchievements();
      if (unsubscribeQuizzes) unsubscribeQuizzes();
    };
  }, [user?.id, realtimeService]);

  // Find most recent quiz for quick actions
  const recentQuiz = recentPlays?.[0] ? {
    id: `quiz_${recentPlays?.[0]?.id}`,
    playTitle: `${recentPlays?.[0]?.title} - Continue`,
    progress: recentPlays?.[0]?.completionPercentage || 0
  } : null;

  // Find recommended play (lowest completion percentage or new play)
  const recommendedPlay = availablePlays?.find(play => !play?.isNew && play?.completionPercentage > 0 && play?.completionPercentage < 100) || 
                         availablePlays?.find(play => play?.isNew) || 
                         availablePlays?.[0];

  useEffect(() => {
    document.title = 'Student Stage Dashboard | BardPlay';
  }, []);

  // Show loading state while auth data is loading
  if (loading || profileLoading || dashboardLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  // Show error if no user is authenticated
  if (!user || !userProfile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-error mb-4">Unable to load user profile</p>
          <button
            onClick={() => window.location.href = '/login'}
            className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
          >
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  // Show error message if data fetch failed
  if (error) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation
          user={{
            id: user?.id,
            name: userProfile?.full_name || userProfile?.email,
            email: userProfile?.email,
            role: userProfile?.role
          }}
          isAuthenticated={true}
          notifications={liveNotifications}
        />
        <main className="main-content">
          <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md">
              <p className="font-semibold">Error loading dashboard</p>
              <p className="text-sm">{error}</p>
              <button 
                onClick={() => window.location?.reload()}
                className="mt-2 text-sm underline"
              >
                Try again
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation
        user={{
          id: user?.id,
          name: userProfile?.full_name || userProfile?.email,
          email: userProfile?.email,
          role: userProfile?.role
        }}
        isAuthenticated={true}
        notifications={liveNotifications}
      />
      <main className="main-content">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <WelcomeHeader
            userName={userProfile?.full_name || userProfile?.email?.split('@')?.[0]}
            xpPoints={currentXp}
            dailyStreak={7}
            lastVisit={userProfile?.updated_at}
            userId={user?.id}
            dailyBonusClaimed={dailyBonusClaimed}
            onStreakMilestone={(days) => {
              triggerStreak(days);
              toastStreak(days);
            }}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2">
              {recentPlays?.length > 0 && (
                <ProgressOverview recentPlays={recentPlays} onPlayComplete={triggerPlayComplete} />
              )}
            </div>
            <div>
              {recentQuiz && recommendedPlay && (
                <QuickActions
                  recentQuiz={recentQuiz}
                  recommendedPlay={{
                    id: recommendedPlay?.id,
                    title: recommendedPlay?.title,
                    reason: recommendedPlay?.isNew ? 'New play available' : 'Continue your journey'
                  }}
                />
              )}
            </div>
          </div>

          {/* Avatar Studio Banner */}
          {!hasExistingAvatar ? (
            <div className="mb-6">
              <button
                onClick={() => navigate('/student-avatar-studio')}
                className="w-full bg-gradient-to-r from-amber-900/40 to-purple-900/40 border border-amber-700/30 rounded-2xl p-5 flex items-center gap-4 hover:from-amber-900/60 hover:to-purple-900/60 transition-all duration-200 group text-left">
                {/* Random mini avatar preview */}
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-stone-800/60 flex-shrink-0 shadow-lg group-hover:scale-105 transition-transform relative border border-amber-700/30">
                  {randomAvatarLayers?.length > 0 ? (
                    randomAvatarLayers?.map((layer, i) => (
                      <img
                        key={`preview-${layer?.slug}-${i}`}
                        src={layer?.publicUrl}
                        alt={layer?.slug}
                        className="absolute inset-0 w-full h-full object-contain"
                        style={{ zIndex: layer?.layer_order }}
                      />
                    ))
                  ) : (
                    <span className="absolute inset-0 flex items-center justify-center text-2xl">🎭</span>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-foreground text-lg">Build Your Avatar</h3>
                  <p className="text-sm text-muted-foreground">Create your Shakespearean character — choose body, face, hair, outfits, and more</p>
                  <p className="text-xs text-amber-400 font-semibold mt-1">✨ Create your avatar and earn 50 XP!</p>
                </div>
                <div className="flex-shrink-0 text-amber-400 group-hover:translate-x-1 transition-transform">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </div>
              </button>
            </div>
          ) : (
            <div className="mb-6">
              <button
                onClick={() => navigate('/student-avatar-studio')}
                className="w-full bg-gradient-to-r from-amber-900/30 to-purple-900/30 border border-amber-700/20 rounded-2xl p-5 flex items-center gap-4 hover:from-amber-900/50 hover:to-purple-900/50 transition-all duration-200 group text-left">
                {/* Saved avatar preview */}
                <div className="w-16 h-16 rounded-xl overflow-hidden bg-stone-800/60 flex-shrink-0 shadow-lg group-hover:scale-105 transition-transform relative border border-amber-600/40">
                  {savedAvatarLayers?.length > 0 ? (
                    savedAvatarLayers?.map((layer, i) => (
                      <img
                        key={`saved-${layer?.slug}-${i}`}
                        src={layer?.publicUrl}
                        alt={layer?.slug}
                        className="absolute inset-0 w-full h-full object-contain"
                        style={{ zIndex: layer?.layer_order }}
                        onError={e => { e.currentTarget.style.display = 'none'; }}
                      />
                    ))
                  ) : (
                    <span className="absolute inset-0 flex items-center justify-center text-2xl">🎭</span>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-foreground text-lg flex items-center gap-2">
                    Your Avatar
                    <span className="text-xs font-normal text-green-400 bg-green-400/10 border border-green-400/20 px-2 py-0.5 rounded-full">Saved</span>
                  </h3>
                  <p className="text-sm text-muted-foreground">Your Shakespearean character is ready for the stage</p>
                  <p className="text-xs text-amber-400/80 mt-1">Tap to customise your look in Avatar Studio</p>
                </div>
                <div className="flex-shrink-0 text-amber-400/60 group-hover:translate-x-1 group-hover:text-amber-400 transition-all">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
                </div>
              </button>
            </div>
          )}

          <div>
            <h2 className="text-2xl font-semibold text-foreground mb-4 flex items-center gap-2">
              Available Plays
            </h2>
            {availablePlays?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {availablePlays?.map((play) =>
                  <PlayCard key={play?.id} play={play} />
                )}
              </div>
            ) : (
              <div className="text-center py-12 bg-card rounded-lg border border-border">
                <p className="text-muted-foreground">No plays available at the moment</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <div className="space-y-6">
              <BadgeCollection badges={badges} />
              <RecentAchievements achievements={achievements} />
            </div>
            <div>
              <LeaderboardPanel
                currentUser={{
                  id: user?.id,
                  name: userProfile?.full_name || userProfile?.email,
                  xp: currentXp,
                  rank: leaderboardUsers?.findIndex(u => u?.id === user?.id) + 1 || leaderboardUsers?.length + 1
                }}
                topUsers={leaderboardUsers}
              />
            </div>
          </div>
        </div>
      </main>

      <CelebrationModal celebration={celebration} onDismiss={dismissCelebration} />
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </div>
  );
};

export default StudentStageDashboard;